import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { GlobalEvents } from '@highlight/ipc'
import { useAuth } from '@renderer/chat/providers'
import { START_CHAT_EVENT } from '@renderer/chat/integrations/conversation-action'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import type { ReminderItem } from '../../../hooks/reminder-mapping'
import { fetchReminder, reminderQueryKey } from '../../../hooks/use-reminder'
import { buildReminderChatPayload } from '../utils/build-draft-prompt'

/**
 * Opens a new chat in the sidepanel about a reminder, from any window: the
 * reminder attached, and its composer asking for the actions it needs, unsent.
 * A reminder planner v2 wrote from a meeting brings the lines that raised it.
 */
export function useStartReminderChat() {
  const fetchSourceLines = useFetchReminderSourceLines()

  return useCallback(
    async (reminder: ReminderItem, trigger: string) => {
      AmplitudeRenderer.trackEvent('Reminder Action', { type: 'Start Chat', reminderId: reminder.id, trigger })
      const sourceLines = await fetchSourceLines(reminder)
      GlobalEvents.emit(START_CHAT_EVENT, buildReminderChatPayload(reminder, sourceLines))
    },
    [fetchSourceLines],
  )
}

/** The transcript lines that raised a reminder, for one planner v2 wrote from a meeting. */
export function useFetchReminderSourceLines() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useCallback(
    async (reminder: ReminderItem): Promise<string | undefined> => {
      if (!reminder.audioSessionId) return undefined
      return queryClient
        .fetchQuery({
          queryKey: reminderQueryKey(reminder.id),
          queryFn: async () => fetchReminder(await getAuthToken(), reminder.id),
          staleTime: 5 * 60 * 1000,
        })
        .then((item) => item?.explanation ?? undefined)
        .catch((error: unknown) => {
          // The chat still opens on the reminder, just without the meeting's lines.
          console.warn('[reminders] could not load the lines that raised the reminder', error)
          return undefined
        })
    },
    [getAuthToken, queryClient],
  )
}
